import { type QueryHookOptions } from "@apollo/client";
import { useSpaceQuery } from "#graphql/supergraphHooks";
import { type SpaceQuery, type SpaceQueryVariables } from "#graphql/supergraphTypes";

/** A single Space as returned by the `space(id)` query (DV-196). */
export type SpaceDetail = NonNullable<SpaceQuery["space"]>;

export interface UseSpaceReturn {
  space: SpaceDetail | null;
  loading: boolean;
  error: ReturnType<typeof useSpaceQuery>["error"];
  refetch: ReturnType<typeof useSpaceQuery>["refetch"];
}

/**
 * DV-196 — fetch a single Space by id. Resolves to `null` on not-found or ACL-denied.
 *
 * @example
 * const { space, loading } = useSpace(spaceId);
 */
export const useSpace = (
  id: string,
  options?: Omit<QueryHookOptions<SpaceQuery, SpaceQueryVariables>, "variables">,
): UseSpaceReturn => {
  // `no-cache`: see useSpaces — read from the network so an updated Space isn't
  // masked by a stale cache entry while cache wiring is deferred (DV-399).
  // Overridable.
  const { data, loading, error, refetch } = useSpaceQuery({
    fetchPolicy: "no-cache",
    ...options,
    variables: { id },
  });

  return {
    space: data?.space ?? null,
    loading,
    error,
    refetch,
  };
};
